import { Button, Link, Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { productRequiresPayment } from "../../App.utils";
import getOrganizations from "../../network/get-organizations";
import getSubscription from "../../network/get-subscription";
import { getUserToken } from "../../utils/account/common";
import { generateAppUrl } from "./utils";
import StepTitleView from "../StepTitleView";
import RegistrationCell from "./components/RegistrationCell";

interface RegistrationConfirmationProps {
  product?: Product;
}

const RegistrationConfirmation = ({
  product,
}: RegistrationConfirmationProps) => {
  const { t } = useTranslation();
  const { data: organization, isLoading: organizationLoading } =
    useQuery<APIResponseGetOrganization>({
      queryKey: ["organization"],
      queryFn: async () => {
        const userToken = (await getUserToken()) as string;
        return getOrganizations(userToken);
      },
    });

  const organizationId = organization?.properties.elements[0].properties.id;
  const { data: subscription, isLoading: subscriptionLoading } = useQuery({
    enabled: !!organization,
    queryKey: ["subscription", organizationId],
    queryFn: async () => {
      const userToken = (await getUserToken()) as string;
      if (!organizationId) {
        return null;
      }
      return getSubscription(userToken, organizationId);
    },
  });

  const companyDetails =
    organization?.properties.elements[0].properties.details;
  const companyName = organization?.properties.elements[0].name;
  const reference = companyDetails?.reference;
  const billing = subscription?.properties.details.billing;
  const invoiceEmail =
    organization?.properties.elements[0].properties.details.invoiceEmails[0];
  const appUrl = generateAppUrl(product);
  const requiresPayment = productRequiresPayment(product);

  const nextDueDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  const formatDate = (date: Date) => {
    const formattedDate = date?.toISOString().split("T");
    return formattedDate[0];
  };

  const companyAddress = `${companyDetails?.streetAddress}, ${companyDetails?.zip} ${companyDetails?.city}`;
  const paymentMethod =
    billing === "send_invoice"
      ? t("step.registrationConfirmation.paymentMethod.invoice.label")
      : t("step.registrationConfirmation.paymentMethod.creditCard.label");

  return (
    <>
      <StepTitleView
        title={t("step.registrationConfirmation.label")}
        descriptions={[t("step.registrationConfirmation.description")]}
      />
      <Stack spacing={2} mt={2}>
        <RegistrationCell
          title={t(
            "step.registrationConfirmation.information.companyName.label"
          )}
          value={companyName || ""}
          hasData={!!organization}
          isLoading={organizationLoading}
        />
        <RegistrationCell
          title={t(
            "step.registrationConfirmation.information.companyAddress.label"
          )}
          value={companyAddress}
          hasData={!!organization}
          isLoading={organizationLoading}
        />
        <RegistrationCell
          title={t("step.registrationConfirmation.information.reference.label")}
          value={reference || ""}
          hasData={!!organization}
          isLoading={organizationLoading}
        />
        {requiresPayment && (
          <>
            <RegistrationCell
              title={t(
                "step.registrationConfirmation.information.invoiceRecipient.label"
              )}
              value={invoiceEmail || ""}
              hasData={!!organization}
              isLoading={organizationLoading}
            />
            <RegistrationCell
              title={t(
                "step.registrationConfirmation.information.paymentMethod.label"
              )}
              value={paymentMethod}
              hasData={!!billing}
              isLoading={!billing}
            />
            <RegistrationCell
              title={t(
                "step.registrationConfirmation.information.nextInvoiceDate.label"
              )}
              value={formatDate(nextDueDate)}
              hasData={!!subscription}
              isLoading={subscriptionLoading}
            />
          </>
        )}
      </Stack>
      <Stack direction="row" justifyContent="center" mt={8}>
        <Button
          id="registration-confirmation-get-started"
          type="submit"
          variant="contained"
          component={Link}
          href={appUrl}
          fullWidth
        >
          {t("button.getStarted")}
        </Button>
      </Stack>
    </>
  );
};

export default RegistrationConfirmation;
